import type { NavSectionProps } from 'src/components/nav-section';
import type { Theme, SxProps, CSSObject, Breakpoint } from '@mui/material/styles';

import { useEffect } from 'react';
import { varAlpha } from 'minimal-shared/utils';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import { styled, useTheme, useMediaQuery } from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import { ConfirmDialog } from 'src/components/custom-dialog/confirm-dialog';
import { NavSectionMini, navSectionCssVars, NavSectionVertical } from 'src/components/nav-section';

import { useChatHistoryContext } from 'src/sections/chat/hooks/useChatHistoryContext';

import { HEADER } from './layoutConfig';
import NavBarIcon from './components/NavBarIcon';
import { NavToggleButton } from './components/nav-toggle-button';

export type NavVerticalProps = React.ComponentProps<'div'> & {
  isNavMini: boolean;
  sx?: SxProps<Theme>;
  cssVars?: CSSObject;
  layoutQuery?: Breakpoint;
  onToggleNav: () => void;
  data: NavSectionProps['data'];
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
};

const navData: NavSectionProps['data'] = [
  {
    subheader: '',
    items: [
      { title: 'Chat Bot', path: '/chat', icon: NavBarIcon('ic-chat') },
      // {
      //   title: 'Prompt Gallery',
      //   path: '/prompt-gallery',
      //   icon: <Iconify icon="solar:shield-check-bold" width={24} height={24} />,
      //   disabled: true,
      // },
    ],
  },
];

const MiniSideNav = () => {
  const { state, setField } = useSettingsContext();
  const theme = useTheme();
  // Check if screen is below md breakpoint
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  // Force mini layout on screens below md
  const isNavMini = state.navLayout === 'mini';
  const isNavHamburger = state.navLayout === 'hamburger';
  const layoutVars = navSectionCssVars.dashboardLayoutVars(theme);
  const confirmDialog = useBoolean();
  const { clearHistory } = useChatHistoryContext();

  useEffect(() => {
    if (!isBelowMd) {
      setField('navLayout', 'mini');
    }
    // eslint-disable-next-line
  }, [isBelowMd]);
  const renderNavVertical = () => (
    <Scrollbar fillContent>
      <NavSectionVertical data={navData} sx={{ px: 2, height: '100px' }} />
      {/* <Box sx={{ px: 2 }}>
        <Stack sx={{
          display: "flex",
          flexDirection: 'row',
          justifyContent: "space-between",
          alignItems: "center",
          padding: '8px 12px',
          borderTop: `1px dashed ${theme.palette.divider}`
        }}>
          <Typography variant="subtitle2" color="var(--text-disabled, #5D6692)" fontWeight={700} fontSize='11px' lineHeight='18px' textTransform='uppercase'>
            Recent Chats
          </Typography>
          <IconButton color="primary" onClick={() => {
            if (history.messages.length > 0) {
              confirmDialog.onTrue()
            }
          }}>
            <Iconify icon='ic:round-plus' color={theme.palette.grey[400]} sx={{ width: '22px', height: '22px' }} />
          </IconButton>
        </Stack> */}

      {/* Recent Chats - For now it is static data*/}
      {/* <Typography sx={{ padding: '0 12px' }} color="#5D6692" fontSize="12px">Today</Typography>
        <Typography sx={{ padding: '0 12px' }} variant='body2' fontWeight={500}>Coming Soon</Typography>
      </Box> */}
    </Scrollbar>
  );

  const renderNavMin = () => (
    <Box sx={{ px: 1 }}>
      <NavSectionMini data={navData} />
      {/* <Box
        sx={{
          borderTop: `1px dashed ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Tooltip title="Add new chat" placement="right" arrow>
          <IconButton
            color="primary"
            onClick={() => {
              if (history.messages.length > 0) {
                confirmDialog.onTrue();
              }
            }}
          >
            <Iconify
              icon="ic:round-plus"
              color={theme.palette.grey[400]}
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Tooltip>
      </Box> */}
    </Box>
  );

  const handleCloseHamburger = () => {
    setField('navLayout', 'close');
  };

  const renderNavHamburgerMenu = () => (
    <Drawer
      open={isNavHamburger}
      onClose={handleCloseHamburger}
      PaperProps={{
        sx: {
          ...layoutVars,
          pt: `${HEADER.H_MOBILE}px`,
          backgroundColor: theme.palette.background.default,
          borderRight: `1px dashed ${theme.palette.divider}`,
          width: 'var(--layout-nav-hamburger-width)',
        },
      }}
    >
      <Scrollbar fillContent>
        <NavSectionVertical data={navData} sx={{ px: 2, flex: '1 1 auto' }} />
      </Scrollbar>
    </Drawer>
  );

  const renderConfirmDialog = () => (
    <ConfirmDialog
      open={confirmDialog.value}
      onClose={confirmDialog.onFalse}
      title="Clear history"
      content={
        <Box display="flex" alignItems="center" gap={1}>
          <Iconify
            icon="material-symbols:info-rounded"
            width={24}
            height={24}
            color={theme.palette.primary.main}
          />
          Are you sure you want to clear your history?
        </Box>
      }
      action={
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            clearHistory();
            confirmDialog.onFalse();
          }}
        >
          Clear
        </Button>
      }
    />
  );

  return (
    <NavRoot
      isNavMini={isNavMini}
      layoutQuery="md"
      sx={{
        ...layoutVars,
        pt: { md: `${HEADER.H_DESKTOP}px`, xs: `${HEADER.H_MOBILE}px` },
        backgroundColor: 'background.default',
        borderRight: `1px dashed ${theme.palette.divider}`,
      }}
    >
      <NavToggleButton
        isNavMini={isNavMini}
        onClick={() => setField('navLayout', state.navLayout === 'vertical' ? 'mini' : 'vertical')}
        sx={[
          () => ({
            display: 'none',
            [theme.breakpoints.up('md')]: { display: 'inline-flex' },
          }),
        ]}
      />
      {isBelowMd ? renderNavHamburgerMenu() : isNavMini ? renderNavMin() : renderNavVertical()}
      {renderConfirmDialog()}
    </NavRoot>
  );
};

const NavRoot = styled('div', {
  shouldForwardProp: (prop: string) => !['isNavMini', 'layoutQuery', 'sx'].includes(prop),
})<Pick<NavVerticalProps, 'isNavMini' | 'layoutQuery'>>(
  ({ isNavMini, layoutQuery = 'md', theme }) => ({
    top: 0,
    left: 0,
    height: '100%',
    display: 'none',
    position: 'fixed',
    flexDirection: 'column',
    zIndex: 1000,
    backgroundColor: 'var(--layout-nav-bg)',
    width: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
    borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
    transition: theme.transitions.create(['width'], {
      easing: 'var(--layout-transition-easing)',
      duration: 'var(--layout-transition-duration)',
    }),
    [theme.breakpoints.up(layoutQuery)]: { display: 'flex' },
  })
);

export default MiniSideNav;
